import queryString from 'query-string';
import type { GridSelectionModel } from '@mui/x-data-grid';

import dash_api from '../../support/dash_api';
import NotificationActions from '../NotificationActions';
import { ReduxAsyncAction } from '../../support/utils';
import type { ActionNamespaceConstants } from 'client/js/redux/types/ReduxAsyncActions.types';
import type { QueryParameters } from '../LinkManager/Types/QueryParameters';
import { LinkFilterTypes } from '../../enums/LinkFilterType';

const FETCH_COLLECTION_NAMESPACE = 'FETCH_COLLECTION' as const;
const FetchCollection = new ReduxAsyncAction(FETCH_COLLECTION_NAMESPACE);
FetchCollection.registerRequest(async (collectionId: string, parameters: Partial<QueryParameters>) => {
  const {
    page,
    size,
    dateTo,
    dateFrom,
    orderBy,
    orderDirection,
    fetchLinkObject,
    archived,
    linkType,
    fetchCollectionCount,
  } = parameters;

  const {
    campaigns,
    channels,
    quickLinkTemplateIds,
    features,
    tags,
    searchString,
    topics,
    analyticsDateTo,
    analyticsDateFrom,
    unique,
    $3p,
    collectionName,
  } = parameters;

  const urlQuery = queryString.stringify({
    page: page,
    page_size: size,
    date_from: dateFrom,
    date_to: dateTo,
    order_by: orderBy,
    order_direction: orderDirection,
    archived: archived,
    fetch_link_object: fetchLinkObject,
    link_type: linkType === LinkFilterTypes.AllLinks ? undefined : linkType,
    fetch_collection_count: fetchCollectionCount,
  });

  const body = {
    campaigns: campaigns,
    channels: channels,
    features: features,
    tags: tags,
    topics: topics,
    search_string: searchString,
    quick_link_template_ids: quickLinkTemplateIds,
    analytics: true,
    analytics_date_to: analyticsDateTo,
    analytics_date_from: analyticsDateFrom,
    unique: unique,
    $3p: $3p,
    collectionId: collectionId,
    collectionName: collectionName,
  };

  try {
    const response = await dash_api.post(`/v1/link/manager/query?${urlQuery}`, body);

    return { collection: response?.data };
  } catch (error) {
    NotificationActions.showNotification('Failed to fetch collection.', 'error');

    throw error;
  }
});

// @ts-ignore
export const FETCH_COLLECTION: ActionNamespaceConstants<typeof FETCH_COLLECTION_NAMESPACE> = FetchCollection.constants;
export const fetchCollection = FetchCollection.request;

const FETCH_OLDEST_LINK_NAMESPACE = 'FETCH_OLDEST_LINK' as const;
const FetchOldestLink = new ReduxAsyncAction(FETCH_OLDEST_LINK_NAMESPACE);
FetchOldestLink.registerRequest(async (collectionId: string) => {
  const urlQuery = queryString.stringify({
    page: 0,
    page_size: 1,
    order_direction: 'asc',
    orderBy: 'created',
    archived: false,
    fetch_collection_count: true,
  });

  const body = {
    collectionId: collectionId,
  };

  try {
    const response = await dash_api.post(`/v1/link/manager/query?${urlQuery}`, body);

    return { oldestLink: response?.data?.links[0] };
  } catch (error) {
    NotificationActions.showNotification('Failed to fetch collection.', 'error');

    throw error;
  }
});

// @ts-ignore
export const FETCH_OLDEST_LINK: ActionNamespaceConstants<typeof FETCH_OLDEST_LINK_NAMESPACE> = FetchOldestLink.constants;
export const fetchOldestLink = FetchOldestLink.request;

const UPDATE_SELECTED_COLLECTION_LINK_IDS_NAMESPACE = 'UPDATE_SELECTED_COLLECTION_LINK_IDS' as const;
const UpdateSelectedCollectionLinkIds = new ReduxAsyncAction(UPDATE_SELECTED_COLLECTION_LINK_IDS_NAMESPACE);
UpdateSelectedCollectionLinkIds.registerRequest(async (selectionModel: GridSelectionModel) => {
  return { selectedCollectionLinkIds: selectionModel };
});

// @ts-ignore
export const UPDATE_SELECTED_COLLECTION_LINK_IDS: ActionNamespaceConstants<
  typeof UPDATE_SELECTED_COLLECTION_LINK_IDS_NAMESPACE
> = UpdateSelectedCollectionLinkIds.constants;
export const updateSelectedCollectionLinkIds = UpdateSelectedCollectionLinkIds.request;

const UPDATE_COLLECTION_LINKS_ACTIVE_COLUMNS_KEYS_NAMESPACE = 'UPDATE_COLLECTION_LINKS_ACTIVE_COLUMNS_KEYS' as const;
const UpdateCollectionLinksActiveColumnsKeys = new ReduxAsyncAction(UPDATE_COLLECTION_LINKS_ACTIVE_COLUMNS_KEYS_NAMESPACE);
UpdateCollectionLinksActiveColumnsKeys.registerRequest(async (columns: string[]) => {
  return { activeColumnKeys: columns };
});
// @ts-ignore
export const UPDATE_COLLECTION_LINKS_ACTIVE_COLUMNS_KEYS: ActionNamespaceConstants<
  typeof UPDATE_COLLECTION_LINKS_ACTIVE_COLUMNS_KEYS_NAMESPACE
> = UpdateCollectionLinksActiveColumnsKeys.constants;
export const updateCollectionLinksActiveColumnsKeys = UpdateCollectionLinksActiveColumnsKeys.request;
